import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, MessageCircle, Save, FileDown } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const DONATION_STATUSES = ["Pledged", "Received", "Thanked"];
const STATUS_COLORS = {
  Pledged: "bg-terracotta/15 text-terracotta-dark",
  Received: "bg-forest/10 text-forest",
  Thanked: "bg-gold/25 text-terracotta-dark",
};

export default function DonationsTab() {
  const [donations, setDonations] = useState([]);
  const [settings, setSettings] = useState({ upi_id: "", upi_name: "", qr_image: "", donation_image: "", founder_image: "" });
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/donations").then((r) => setDonations(r.data)).catch((e) => toast.error(formatApiError(e, "Failed to load donations.")));
    api.get("/settings").then((r) => setSettings({
      upi_id: r.data.upi_id || "",
      upi_name: r.data.upi_name || "",
      qr_image: r.data.qr_image || "",
      donation_image: r.data.donation_image || "",
      founder_image: r.data.founder_image || "",
    })).catch(() => {});
  }, []);

  useEffect(() => load(), [load]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("Donation settings saved — the QR on the Donate page is updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingSettings(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/donations/${id}`, { status });
      setDonations((list) => list.map((d) => (d.id === id ? { ...d, status } : d)));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const waDigits = (p) => {
    let d = (p || "").replace(/\D/g, "");
    if (d.length === 10) d = `91${d}`;
    return d;
  };

  const downloadReceipt = async (d) => {
    try {
      const r = await api.get(`/admin/donations/${d.id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `80G-Receipt-${d.transaction_id || d.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("80G receipt downloaded");
    } catch (e) {
      toast.error("Receipt is available once the donation is marked Received.");
    }
  };

  const month = new Date().toISOString().slice(0, 7);
  const num = (v) => Number(v) || 0;
  const received = donations.filter((d) => d.status === "Received");
  const stats = [
    { label: "Received This Month", value: received.filter((d) => d.created_at?.startsWith(month)).reduce((s, d) => s + num(d.amount), 0), testid: "donations-stat-month" },
    { label: "Total Received", value: received.reduce((s, d) => s + num(d.amount), 0), testid: "donations-stat-total" },
    { label: "Pending Pledges", value: donations.filter((d) => d.status === "Pledged").reduce((s, d) => s + num(d.amount), 0), testid: "donations-stat-pending" },
  ];

  return (
    <div data-testid="donations-tab">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="donations-stats">
        {stats.map((s) => (
          <div key={s.label} data-testid={s.testid} className="rounded-2xl border border-forest/10 bg-white p-6">
            <p className="font-serif text-3xl font-bold text-forest-deep">₹{s.value.toLocaleString("en-IN")}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.14em] text-stone-500">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-forest/10 bg-white p-6" data-testid="donation-settings-card">
        <p className="font-display font-semibold text-forest-deep">Donation QR Settings</p>
        <p className="mt-1 text-sm text-stone-500">
          Add your UPI ID and the Donate page generates a scannable QR automatically (amount pre-filled). Or paste a QR image URL to show your printed QR instead.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">UPI ID</label>
            <Input value={settings.upi_id} onChange={(e) => setSettings((s) => ({ ...s, upi_id: e.target.value.trim() }))} placeholder="trust@upi" data-testid="settings-upi-id" className="mt-1.5 bg-white" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Payee Name</label>
            <Input value={settings.upi_name} onChange={(e) => setSettings((s) => ({ ...s, upi_name: e.target.value }))} placeholder="St Mary's Charitable Trust" data-testid="settings-upi-name" className="mt-1.5 bg-white" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">QR Image URL (optional)</label>
            <Input value={settings.qr_image} onChange={(e) => setSettings((s) => ({ ...s, qr_image: e.target.value.trim() }))} placeholder="https://... (overrides generated QR)" data-testid="settings-qr-image" className="mt-1.5 bg-white" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Donation Page Photo URL</label>
            <Input value={settings.donation_image} onChange={(e) => setSettings((s) => ({ ...s, donation_image: e.target.value.trim() }))} placeholder="https://... (hero photo)" data-testid="settings-donation-image" className="mt-1.5 bg-white" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Founder Photo URL</label>
            <Input value={settings.founder_image} onChange={(e) => setSettings((s) => ({ ...s, founder_image: e.target.value.trim() }))} placeholder="https://... (About page)" data-testid="settings-founder-image" className="mt-1.5 bg-white" />
          </div>
        </div>
        <Button onClick={saveSettings} disabled={savingSettings} data-testid="settings-save-button" className="mt-4 rounded-full bg-forest px-6 font-display text-cream hover:bg-forest-deep disabled:opacity-60">
          <Save className="mr-2 h-4 w-4" /> {savingSettings ? "Saving..." : "Save Settings"}
        </Button>
      </div>

      <div className="mt-6 space-y-3" data-testid="donations-list">
        {donations.map((d) => (
          <div key={d.id} data-testid={`donation-row-${d.id}`} className="flex flex-col gap-4 rounded-2xl border border-forest/10 bg-white p-5 sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-display font-semibold text-forest-deep">{d.name}</p>
                {d.amount && <span className="rounded-full bg-terracotta/15 px-3 py-0.5 text-[11px] font-bold text-terracotta-dark">₹{Number(d.amount).toLocaleString("en-IN")}</span>}
                <span className="rounded-full bg-linen px-3 py-0.5 text-[11px] font-medium text-forest">{d.cause}</span>
              </div>
              <p className="mt-1 text-sm text-stone-500">
                {d.phone} {d.email ? `· ${d.email}` : ""} · {d.created_at?.slice(0, 10)}
              </p>
              {d.transaction_id && (
                <p className="mt-1 text-xs text-stone-500">
                  UPI UTR: <span className="font-mono font-semibold text-forest">{d.transaction_id}</span>
                </p>
              )}
              {d.message && <p className="mt-1.5 text-sm italic text-stone-600">"{d.message}"</p>}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={() => downloadReceipt(d)}
                disabled={d.status !== "Received"}
                data-testid={`donation-receipt-${d.id}`}
                className="rounded-full border border-gold/50 p-2 text-terracotta-dark hover:bg-gold hover:text-forest-deep disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Download 80G receipt"
                title={d.status === "Received" ? "Download 80G receipt (PDF)" : "Mark as Received to issue the 80G receipt"}
              >
                <FileDown className="h-4 w-4" />
              </button>
              <a href={`tel:${d.phone}`} data-testid={`donation-call-${d.id}`} className="rounded-full border border-forest/20 p-2 text-forest hover:bg-forest hover:text-cream" aria-label="Call">
                <Phone className="h-4 w-4" />
              </a>
              <a href={`https://wa.me/${waDigits(d.phone)}`} target="_blank" rel="noreferrer" data-testid={`donation-whatsapp-${d.id}`} className="rounded-full border border-forest/20 p-2 text-forest hover:bg-forest hover:text-cream" aria-label="WhatsApp">
                <MessageCircle className="h-4 w-4" />
              </a>
              <select
                value={d.status}
                onChange={(e) => updateStatus(d.id, e.target.value)}
                data-testid={`donation-status-${d.id}`}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${STATUS_COLORS[d.status] || "bg-stone-100 text-stone-600"} focus:outline-none`}
              >
                {DONATION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        ))}
        {donations.length === 0 && (
          <p className="rounded-2xl border border-dashed border-forest/20 bg-white py-14 text-center text-sm text-stone-500" data-testid="donations-empty">
            No donation pledges yet. Share the Donate page to begin.
          </p>
        )}
      </div>
    </div>
  );
}
